import { AvatarSource } from 'graphql/generated/scheme'

interface AvatarUser {
	avatarUrl?: string | null
	avatarSource?: AvatarSource | null
	instagramAvatarUrl?: string | null
	tikTokAvatarUrl?: string | null
	twitchAvatarUrl?: string | null
}

/** Connected-account sources, in the order an unset/System preference falls back to them. */
const CONNECTED_AVATAR_SOURCES = [AvatarSource.Instagram, AvatarSource.TikTok, AvatarSource.Twitch] as const

export function isConnectedAvatarSource(source?: AvatarSource | null): boolean {
	return CONNECTED_AVATAR_SOURCES.some(connected => connected === source)
}

function connectedAvatarUrl(user: AvatarUser | null | undefined, source: AvatarSource): string | null | undefined {
	switch (source) {
		case AvatarSource.Instagram:
			return user?.instagramAvatarUrl
		case AvatarSource.TikTok:
			return user?.tikTokAvatarUrl
		case AvatarSource.Twitch:
			return user?.twitchAvatarUrl
		default:
			return undefined
	}
}

/**
 * Resolves which source is actually providing the displayed avatar, which can differ
 * from the raw stored `avatarSource` preference: an explicit Instagram/TikTok/Twitch choice
 * with no image of its own falls back to System, and an unset/System preference with
 * no upload falls back to whichever connected account has an image. UI that shows a
 * "selected" state should use this, not the raw preference, so it never highlights a
 * source that isn't the one actually on screen.
 */
export function resolveEffectiveAvatarSource(user?: AvatarUser | null): AvatarSource {
	const preferred = user?.avatarSource
	if (preferred && isConnectedAvatarSource(preferred) && connectedAvatarUrl(user, preferred)) {
		return preferred
	}
	if (user?.avatarUrl) {
		return AvatarSource.System
	}
	// Explicit connected-source preference with no image of its own: honor that choice
	// and land on System (a placeholder) rather than silently swapping to another
	// connected account. Only System/unset falls back to whichever one has an image.
	if (isConnectedAvatarSource(preferred)) {
		return AvatarSource.System
	}
	return CONNECTED_AVATAR_SOURCES.find(source => connectedAvatarUrl(user, source)) ?? AvatarSource.System
}

export function resolveAvatarSrc(user?: AvatarUser | null): string | null | undefined {
	const source = resolveEffectiveAvatarSource(user)
	return source === AvatarSource.System ? user?.avatarUrl : connectedAvatarUrl(user, source)
}

/**
 * The user's own avatar, for an image to fall back to when a connected account's photo
 * fails to load (TikTok CDN links can expire). Null when the resolved avatar already is
 * the user's own, so there is nothing different to fall back to.
 */
export function resolveAvatarFallbackSrc(user?: AvatarUser | null): string | null {
	if (resolveEffectiveAvatarSource(user) === AvatarSource.System) return null
	return user?.avatarUrl?.trim() || null
}
